/**
 * THE ONE SET OF WORDS FOR WHERE A BODY OF WORK IS IN ITS LIFE — P2.
 *
 *   Pipeline → In delivery → In service
 *
 * A board, and an epic that rides on boards, moves along this line: it is a
 * CANDIDATE being shaped (Pipeline), then it is being BUILT (In delivery), then
 * it has shipped and is being RUN (In service). This file is the single source
 * of those three words, mirroring the `PhaseStatus` / `MONTHS` pattern — a type,
 * an ordered array, and a `Record<tier, label>` — so no screen spells a tier its
 * own way. The pipeline columns, the portfolio lifecycle lens, an epic's roll-up
 * mode and the status pack all read their labels from here.
 *
 * WHY A GATE GUARD. These words are new to the product and will be typed onto
 * many surfaces as the pipeline (#inc4) and Run/BAU (#435) work lands. The guard
 * in scripts/test-the-lifecycle-words-have-one-home.ts fails the build if any
 * `src` file hard-codes one of them as a display string, so two surfaces can
 * never drift to two spellings of the same tier — the standing consistency rule,
 * enforced rather than hoped for.
 *
 * SCOPE IN THIS INCREMENT. The constant and the labels are the firm part. The
 * DERIVATION of a board's tier below is deliberately minimal — it reads the
 * board's existing project state (is it running as a project, has it gone live) —
 * because the richer pipeline-board and continuous/BAU detection belong to the
 * later increments that own those features. It is marked so, and kept derived so
 * it can never disagree with the board it describes.
 */

import { BOARD_COLUMNS, STATUS_ORDER, type Issue, type IssueStatus } from './types';

export type LifecycleTier = 'pipeline' | 'indelivery' | 'inservice';

/** The order the tiers run in — read by the lens to lay its bands out. */
export const LIFECYCLE_TIERS: LifecycleTier[] = ['pipeline', 'indelivery', 'inservice'];

/**
 * The display words. THE ONLY PLACE THESE THREE STRINGS LIVE. A screen that
 * wants to show a tier imports this and indexes it; it never writes the words.
 */
export const LIFECYCLE_LABELS: Record<LifecycleTier, string> = {
  pipeline: 'Pipeline',
  indelivery: 'In delivery',
  inservice: 'In service',
};

/** Where a tier sits on the line, for ordering and "least-advanced" comparisons. */
export const LIFECYCLE_RANK: Record<LifecycleTier, number> = {
  pipeline: 0,
  indelivery: 1,
  inservice: 2,
};

/**
 * WHAT A BOARD'S TIER IS, from the project state it already carries.
 *
 * Minimal and derived (see the file header): a board that is not yet being run
 * as a project is a candidate — `pipeline`; one that is running as a project and
 * has not reached the far side of go-live is being built — `indelivery`; one
 * that has gone live and whose delivery is finished is being run — `inservice`.
 * `atService` is the board-level go-live judgement the caller already computes
 * (every live delivery phase done and on/after go-live); kept as an argument so
 * this file does not reach back into the portfolio's heavier machinery.
 */
export function boardLifecycleTier(input: {
  tracked: boolean;
  atService: boolean;
}): LifecycleTier {
  if (!input.tracked) return 'pipeline';
  if (input.atService) return 'inservice';
  return 'indelivery';
}

/**
 * IS THIS BOARD RUN AS A PROJECT? — #435, the one gate every project-shape
 * surface reads.
 *
 * A board is run either as a bounded PROJECT (a plan, phases, milestones, a
 * go-live) or as a CONTINUOUS service (keep-the-lights-on flow with no plan).
 * This is a different axis from `tracked` above: `tracked` asks whether a
 * project HAS a plan yet (pipeline vs in-delivery); this asks whether the board
 * is MEANT to be run as a project at all. ABSENT READS AS PROJECT, because
 * every board predating #435 ran as one and the column defaults to `project` —
 * so a board shows as a project until someone explicitly chooses continuous.
 *
 * Reading it in ONE place keeps "project mode gates everything" — the Plan tab,
 * phases, milestones, the pipeline columns — consistent, rather than each
 * screen re-deriving `deliveryMode === 'project'` and some getting the
 * absent-means-project rule wrong.
 */
export function isContinuous(board: {
  deliveryMode?: 'project' | 'continuous' | null;
}): boolean {
  return board.deliveryMode === 'continuous';
}

export function isProjectMode(board: {
  deliveryMode?: 'project' | 'continuous' | null;
}): boolean {
  return !isContinuous(board);
}

/**
 * WHETHER THE OPTIONAL "IN STAGING" COLUMN IS ON for this board — #471.
 *
 * Absent/false reads as off, so every board that predates this (and every board
 * that never turns it on) behaves exactly as today: no staging lane, and no card
 * can be moved to `staging`. The one place board surfaces read the opt-in,
 * mirroring isProjectMode, so the lane appears and disappears consistently.
 */
export function stagingEnabled(board: { stagingEnabled?: boolean | null }): boolean {
  return board.stagingEnabled === true;
}

/**
 * THE BOARD'S VISIBLE LANES for this board — BOARD_COLUMNS, with the In Staging
 * lane spliced in between In Progress and In Review only when the board has
 * turned it on, and the Done lane dropped while In Staging is on if the board
 * has chosen to hide it (hideDoneInStaging, 0183). Every lane-list read goes
 * through here, so the columns are derived in exactly one place.
 *
 * Hiding Done touches the BOARD COLUMN only — statusOrderFor still offers `done`,
 * so a card is always completable from its own status picker, and Done returns
 * the instant In Staging is turned off.
 */
export function boardColumnsFor(board: {
  stagingEnabled?: boolean | null;
  hideDoneInStaging?: boolean | null;
  backlogOnBoard?: boolean | null;
}): IssueStatus[] {
  const cols = [...BOARD_COLUMNS];
  if (stagingEnabled(board)) {
    const at = cols.indexOf('inreview');
    cols.splice(at < 0 ? cols.length : at, 0, 'staging');
    if (board.hideDoneInStaging === true) {
      const done = cols.indexOf('done');
      if (done >= 0) cols.splice(done, 1);
    }
  }
  // The backlog is the holding pen, not open work: shown as a column only when the
  // board opts in (0218), spliced at the FRONT so To-Do stays the first open bucket.
  // It never enters open/WIP counts — those read the statuses, not this lane list —
  // and statusOrderFor is unchanged (backlog was always a pickable status).
  if (board.backlogOnBoard === true) cols.unshift('backlog');
  return cols;
}

/**
 * THE STATUSES A PICKER MAY OFFER on this board — STATUS_ORDER, with `staging`
 * dropped unless the board turned the In Staging column on. Every status menu,
 * filter and grouping reads this so a board without the column never offers a
 * move to a lane it does not show, and no staging-less board sees the word.
 */
export function statusOrderFor(board: { stagingEnabled?: boolean | null }): IssueStatus[] {
  if (stagingEnabled(board)) return STATUS_ORDER;
  return STATUS_ORDER.filter((s) => s !== 'staging');
}

/**
 * AN EPIC'S TIER, for the portfolio lens that groups epics by where they are.
 *
 * An epic spans boards, so it has no single tier of its own; it reads the tier
 * of the board holding the plurality of its cards — the same "where most of the
 * work is" rule the derived owner uses, so the two agree. Ties fall to the
 * least-advanced tier, because an epic is only as shipped as its least-shipped
 * centre of gravity. `null` when the epic has no cards on any board.
 */
export function epicLifecycleTier(boardTiers: LifecycleTier[]): LifecycleTier | null {
  if (boardTiers.length === 0) return null;
  const count = new Map<LifecycleTier, number>();
  for (const t of boardTiers) count.set(t, (count.get(t) ?? 0) + 1);
  let best: LifecycleTier | null = null;
  for (const t of LIFECYCLE_TIERS) {
    const c = count.get(t) ?? 0;
    if (c === 0) continue;
    if (best === null || c > (count.get(best) ?? 0)) best = t;
    // On a tie, LIFECYCLE_TIERS order means the earlier (less-advanced) tier was
    // seen first and stays, so ties already fall to the least-advanced tier.
  }
  return best;
}

/**
 * THE MOMENT WORK BEGAN, recorded the first time a card LEAVES THE QUEUE.
 *
 * Not only "reached In Progress": a card pulled straight into In Review or In
 * Staging, or marked Done in one move, has still started. The only statuses that
 * are NOT a start are the queue a card waits in before work (Backlog, To-do) and
 * Won't-do, which is abandoned without ever being worked. Stamping the first exit
 * from the queue — wherever it lands — is what lets cycle time exist for the ~3 in
 * 4 finished cards that never pass through In Progress (measured, Phase 0). Vinnie,
 * 2 Nov 2026: "the minute a card leaves backlog or to-do, it is tracked no matter
 * where it goes."
 *
 * The store merges this into the patch it writes; it returns a patch, or nothing
 * at all — and "nothing" is the important half. A card pulled back into the queue
 * and started again has not started twice, so this only ever fills a blank and
 * never overwrites one. Cycle time measured from the SECOND start would quietly
 * shorten every number on a board where work gets bounced back, which is exactly
 * the board where the number matters most.
 */
export function whenWorkBegan(before: Issue | undefined, status: IssueStatus): { startedAt?: number } {
  if (!before || before.startedAt) return {};
  // Still in the queue (Backlog ↔ To-do) or abandoned (Won't-do): not a start.
  if (status === 'backlog' || status === 'todo' || status === 'wontdo') return {};
  /*
   * NEVER BEFORE THE CARD EXISTED, whatever this device's clock says.
   *
   * `createdAt` is stamped by Postgres; this is stamped by the browser, and the
   * two are not the same clock. A machine five seconds behind the server — which
   * is ordinary, and was exactly the case on 20 August 2026 — produces a card
   * that started before it was made. Cycle time is then measured from a moment
   * that never happened, and comes out short or negative on precisely the boards
   * where somebody is looking at it.
   *
   * Clamping keeps the one guarantee that matters: work cannot begin before the
   * card does. A device that is AHEAD is left alone, because being a moment late
   * to notice is not the same kind of wrong as time running backwards.
   */
  return { startedAt: Math.max(Date.now(), before.createdAt + 1) };
}
